/* eslint-disable react/prop-types */ // TODO: add props
import { useMemo } from 'react';
import PT from 'prop-types';
import IPT from 'react-immutable-proptypes';
import { Link } from 'react-router-dom';
import EPT from 'edrolo/propTypes';
import { QUESTION_GROKKED } from 'edrolo/domains/exams';
import { RowExpansionArrow } from '../../../components';
import { ResultsTable } from '../../../components/ResultsTable';
import { IndicatorCell, INDICATOR_TYPES } from '../../../../../components';
import { StudentResponseBars } from '../StudentResponseBars';
import { getProfileQuestionResponseSummary } from '../../../utils';
import style from './ShortAnswerQuestionReview.scss';

export const ShortAnswerQuestionReview = ({
  question,
  solution,
  responses,
  highlightProfileId,
  getStudentCourseProgressUrl,
  classStudentProfiles,
  insights,
}) => {
  const profileQuestionResponseSummary = useMemo(
    () =>
      getProfileQuestionResponseSummary(
        classStudentProfiles,
        responses,
        solution,
        question
      ),
    [classStudentProfiles, responses, solution, question]
  );

  const selfMarkedQuestion = insights?.self_marked_questions?.find(
    (q) => q.question_id === question.get('id')
  );

  const questionMark = (+question.get('marks')).toFixed(0);

  const resultsTableColumns = [
    { id: 'leftSpacer', style: { width: '2%' } },

    {
      id: 'name',
      Header: 'Name',
      style: { width: '25%' },
      accessor: (row) => (
        <Link
          to={getStudentCourseProgressUrl(row.get('userProfileId'))}
        >{`${row.get('userFirstName')} ${row.get('userLastName')}`}</Link>
      ),
      sortType: (rowA, rowB) =>
        rowA.original.get('userLastName').toLowerCase() >
        rowB.original.get('userLastName').toLowerCase()
          ? 1
          : -1,
    },

    {
      id: 'studentMark',
      Header: 'Student Mark',
      style: { width: '20%' },
      accessor: (row) => {
        const studentMarks = (+row.getIn([
          'response',
          'text_marks_from_student',
        ])).toFixed(0);
        if (studentMarks && !isNaN(studentMarks)) {
          return `${studentMarks}/${questionMark}`;
        }

        return 'N/A';
      },
      sortType: (rowA, rowB) =>
        rowA.original.getIn(['response', 'marks_final']) >
        rowB.original.getIn(['response', 'marks_final'])
          ? 1
          : -1,
    },

    {
      id: 'studentResponse',
      Header: () => (
        <div className={style.studentResponseHeader}>Student Response</div>
      ),
      style: { width: '35%' },
      accessor: (row) => {
        return {
          responseText: row.get('answer'),
        };
      },
      Cell: ({ cell, row }) => {
        const { responseText } = cell.value;
        if (row.isExpanded && responseText) {
          return (
            <div
              className={style.responseWrapper}
              {...row.getToggleRowExpandedProps()}
            >
              <span>{responseText}</span>
              &nbsp;&nbsp;&nbsp;&nbsp;
              <RowExpansionArrow expanded />
            </div>
          );
        } else if (!row.isExpanded && responseText) {
          return (
            <div
              className={style.responseWrapper}
              {...row.getToggleRowExpandedProps()}
            >
              <IndicatorCell
                value={'Answered'}
                indicatorType={INDICATOR_TYPES.POSITIVE}
              />
              &nbsp;&nbsp;&nbsp;&nbsp;
              <RowExpansionArrow />
            </div>
          );
        } else if (!responseText) {
          return (
            <IndicatorCell
              value={'Not answered'}
              indicatorType={INDICATOR_TYPES.NEUTRAL}
            />
          );
        }
      },
      disableSortBy: true,
    },

    {
      id: 'selfReflection',
      Header: 'Self-reflection',
      style: { width: '15%' },
      accessor: (row) => {
        if (row.get('grokked') === QUESTION_GROKKED.YES) {
          return 'Understands';
        }
        if (row.get('grokked') === QUESTION_GROKKED.NO) {
          return 'Needs help';
        }
        return 'Not reflected';
      },
      Cell: (cell) => {
        if (cell.row.original.get('grokked') === QUESTION_GROKKED.YES) {
          return (
            <IndicatorCell
              value={cell.value}
              indicatorType={INDICATOR_TYPES.POSITIVE}
            />
          );
        }
        if (cell.row.original.get('grokked') === QUESTION_GROKKED.NO) {
          return (
            <IndicatorCell
              value={cell.value}
              indicatorType={INDICATOR_TYPES.NEGATIVE}
            />
          );
        }
        return (
          <IndicatorCell
            value={cell.value}
            indicatorType={INDICATOR_TYPES.NEUTRAL}
          />
        );
      },
      sortType: (rowA, rowB) => {
        const sortOrder = [
          QUESTION_GROKKED.YES,
          QUESTION_GROKKED.NO,
          QUESTION_GROKKED.NO_RESPONSE,
          null,
        ];

        if (
          sortOrder.indexOf(rowA.original.get('grokked')) >
          sortOrder.indexOf(rowB.original.get('grokked'))
        ) {
          return 1;
        }
        return -1;
      },
    },

    { id: 'rightSpacer', style: { width: '2%' } },
  ];

  return (
    <>
      <div className={style.container}>
        <div>
          <h3 className={style.heading}>How students responded</h3>
          <StudentResponseBars
            profileQuestionResponseSummary={profileQuestionResponseSummary}
          />
        </div>
      </div>
      {selfMarkedQuestion && (
        <div className={style.content}>
          {`Average class mark:`}{' '}
          <span>
            {`${selfMarkedQuestion.average_marks.toFixed(1)}/${
              selfMarkedQuestion.total_marks_available
            }`}
          </span>
        </div>
      )}
      <div>
        <h3 className={style.heading}>Response details</h3>
        <ResultsTable
          data={profileQuestionResponseSummary}
          columns={resultsTableColumns}
          highlightProfileId={highlightProfileId}
        />
      </div>
    </>
  );
};

ShortAnswerQuestionReview.propTypes = {
  classStudentProfiles: IPT.orderedMapOf(
    IPT.contains({
      user_id: PT.number.isRequired,
      profile_id: PT.number.isRequired,
      first_name: PT.string.isRequired,
      last_name: PT.string.isRequired,
      display_name: PT.string,
      is_active: PT.bool,
    })
  ),
  responses: IPT.listOf(EPT.ExamResponse),
  solution: IPT.contains({
    solution: IPT.contains({
      exemplar_response_checklist_items: IPT.listOf(
        IPT.contains({
          id: PT.number.isRequired,
          description: PT.string.isRequired,
        })
      ),
    }),
  }).isRequired,
  highlightProfileId: PT.number,
  getStudentCourseProgressUrl: PT.func,
  insights: PT.object,
};
